import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { Download } from 'lucide-react';
import { FC, memo, useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ContractDocument } from 'src/entities/contractEntity';

// PDF.js workerの設定
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type Props = {
  open: boolean;
  onClose: () => void;
  documents: ContractDocument[];
  contractorName?: string;
};

const DocumentPreviewDialog: FC<Props> = memo(({
  open,
  onClose,
  documents,
  contractorName,
}) => {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentDocument = documents[currentTabIndex];

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTabIndex(newValue);
    setError(null);
    setLoading(true);
    setNumPages(0);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF読み込みエラー:', error);
    setError('PDFファイルの読み込みに失敗しました');
    setLoading(false);
  }, []);

  const handleDownload = useCallback(() => {
    if (!currentDocument?.url) return;

    // 新しいタブで開く（ブラウザのダウンロード機能を利用）
    const link = document.createElement('a');
    link.href = currentDocument.url;
    link.download = currentDocument.filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [currentDocument]);

  const isPdf = useCallback((filename: string) => {
    return filename.toLowerCase().endsWith('.pdf');
  }, []);

  // PDFでない場合の判定
  const isNotPdf = currentDocument && !isPdf(currentDocument.filename);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: { minHeight: '40vh', maxHeight: '90vh' } }}}
    >
      <DialogTitle>
        契約書プレビュー{contractorName && ` - ${contractorName}`}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* タブ（複数ファイルの場合のみ表示） */}
        {documents.length > 1 && (
          <Tabs
            value={currentTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
          >
            {documents.map((doc) => (
              <Tab
                key={doc.url}
                label={doc.filename}
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Tabs>
        )}

        {/* コンテンツエリア */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            p: 2,
            bgcolor: 'grey.100',
          }}
        >
          {/* エラー表示 */}
          {error && (
            <Alert severity="error" sx={{ width: '100%', maxWidth: 600 }}>
              {error}
            </Alert>
          )}

          {/* PDFでないファイルの場合 */}
          {!error && isNotPdf && (
            <Alert severity="info" sx={{ width: '100%', maxWidth: 600 }}>
              <Typography variant="body2" gutterBottom>
                このファイルはプレビューできません: {currentDocument.filename}
              </Typography>
              <Typography variant="body2">
                ダウンロードボタンからファイルを取得してください。
              </Typography>
            </Alert>
          )}

          {/* PDF表示 */}
          {!error && !isNotPdf && currentDocument && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              <Document
                file={currentDocument.url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<CircularProgress />}
              >
                {Array.from(new Array(numPages), (_el, index) => (
                  <Box key={`page_${index + 1}`} sx={{ mb: 2, boxShadow: 3 }}>
                    <Page
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={800}
                    />
                  </Box>
                ))}
              </Document>

              {numPages > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  全 {numPages} ページ
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<Download size={18} />}
          onClick={handleDownload}
          variant="outlined"
          disabled={!currentDocument}
        >
          ダウンロード
        </Button>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
});

DocumentPreviewDialog.displayName = 'DocumentPreviewDialog';

export default DocumentPreviewDialog;
